import React from 'react';
import { Badge } from '../../../../ui/badge';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { RedressGuidanceSection as RedressGuidanceSectionType } from '../../../../../types/case-workspace';
import ReportSectionHeading from './ReportSectionHeading';

interface RedressGuidanceSectionProps {
  guidance: RedressGuidanceSectionType;
  onDecisionClick?: (decisionIdOrReference: string) => void;
}

const RedressGuidanceSection: React.FC<RedressGuidanceSectionProps> = ({ guidance, onDecisionClick }) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <section className="border-b border-border px-6 py-5">
      <ReportSectionHeading number={4} title="Redress Guidance (Indicative)" className="mb-4" />

      {/* Monetary Range */}
      <div className="mb-4 rounded-lg border border-[var(--go-warm-border)] bg-background p-4">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">Monetary Range</h3>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-base font-semibold">
            {formatCurrency(guidance.monetaryRange.min)} - {formatCurrency(guidance.monetaryRange.max)}
          </Badge>
        </div>
      </div>

      {/* Remedies */}
      {guidance.remedies && guidance.remedies.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">Remedies</h3>
          <ul className="space-y-2">
            {guidance.remedies.map((remedy, index) => (
              <li key={index} className="flex items-start gap-2">
                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                <p className="text-sm text-foreground">{remedy}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Compensation Types */}
      {guidance.types && guidance.types.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">Compensation Types</h3>
          <div className="flex flex-wrap gap-2">
            {guidance.types.map((type, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {type.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {guidance.notes && (
        <div className="mb-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {guidance.notes}
          </p>
        </div>
      )}

      {/* Compensation Breakdown (if available) */}
      {guidance.compensationBreakdown && (
          <div className="mb-4 rounded-lg border border-[var(--go-warm-border)] bg-[var(--go-spring)] p-4">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">Compensation Breakdown</h3>
          <dl className="space-y-3 text-sm">
            {guidance.compensationBreakdown.compensation_for_loss && guidance.compensationBreakdown.compensation_for_loss !== 'none' && guidance.compensationBreakdown.compensation_for_loss !== 'null' && (
              <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
                <dt className="text-muted-foreground">Compensation for loss:</dt>
                <dd className="font-medium text-foreground text-right">
                  {(() => {
                    const lossValue = guidance.compensationBreakdown.compensation_for_loss;
                    // Try to parse as number and format, otherwise display as-is
                    const numValue = typeof lossValue === 'string' 
                      ? parseFloat(lossValue.replace(/[£,\s]/g, ''))
                      : typeof lossValue === 'number' 
                        ? lossValue 
                        : null;
                    return numValue && !isNaN(numValue) 
                      ? formatCurrency(numValue)
                      : lossValue;
                  })()}
                </dd>
              </div>
            )}
            {guidance.compensationBreakdown.compensation_for_distress != null && guidance.compensationBreakdown.compensation_for_distress > 0 && (
              <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
                <dt className="text-muted-foreground">Compensation for distress:</dt>
                <dd className="font-medium text-foreground text-right">
                  {formatCurrency(guidance.compensationBreakdown.compensation_for_distress)}
                </dd>
              </div>
            )}
            {guidance.compensationBreakdown.non_financial_award && guidance.compensationBreakdown.non_financial_award !== 'none' && guidance.compensationBreakdown.non_financial_award !== 'null' && (
              <div className="grid grid-cols-[1fr_2fr] gap-4 items-start">
                <dt className="text-muted-foreground">Non-financial award:</dt>
                <dd className="font-medium text-foreground">
                  {guidance.compensationBreakdown.non_financial_award}
                </dd>
              </div>
            )}
          </dl>
          {guidance.compensationBreakdown.justification && (
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              {guidance.compensationBreakdown.justification}
            </p>
          )}
        </div>
      )}

      {/* Precedent References */}
      {guidance.precedents && guidance.precedents.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">Based on precedents:</h3>
          <div className="flex flex-wrap gap-2">
            {guidance.precedents.map((ref) => (
              onDecisionClick ? (
                <button
                  key={ref}
                  onClick={() => onDecisionClick(ref)}
                  className="text-xs text-primary hover:underline cursor-pointer"
                >
                  {ref}
                </button>
              ) : (
                <a
                  key={ref}
                  href={`#precedent-${ref}`}
                  className="text-xs text-primary hover:underline"
                >
                  {ref}
                </a>
              )
            ))}
          </div>
        </div>
      )}

      {/* Warning Message */}
      <div className="mt-4 flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
        <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-orange-900 mb-1">
            Guidance Only
          </p>
          <p className="text-sm text-orange-800 leading-relaxed">
            This is guidance only. Final Ombudsman awards may differ based on individual circumstances and additional evidence.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RedressGuidanceSection;
